import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { pillarArticles } from '@/mocks/articles';

export default function PillarArticle() {
  const { slug } = useParams();
  const { t } = useTranslation();

  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="w-full px-4 md:px-6 lg:px-10 py-24 text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Guide not found
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            The guide you are looking for does not exist.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Back to Homepage
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" />
        </div>
        <div className="relative w-full px-4 md:px-6 lg:px-10 py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-primary-500/80 text-background-50 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto">
              {article.excerpt}
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700">{article.category}</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700 line-clamp-1">{article.title}</span>
          </nav>

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {article.readTime}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.updated}
            </span>
          </div>

          <div className="prose prose-lg max-w-none">
            {article.sections.map((section, i) => (
              <div key={i}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-5">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="text-base text-foreground-600 leading-relaxed mb-5">
                    {p}
                  </p>
                ))}
                {section.list && (
                  <ul className="space-y-3 mb-6">
                    {section.list.map((item, k) => (
                      <li key={k} className="flex items-start gap-3">
                        <i className="ri-check-line text-primary-500 mt-1 shrink-0" />
                        <span className="text-base text-foreground-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Safety Note */}
            <div className="bg-accent-50 border-l-4 border-accent-500 p-6 rounded-r-lg my-10">
              <p className="text-sm text-accent-800 font-medium mb-2">Important Note</p>
              <p className="text-sm text-accent-700 leading-relaxed">
                This is an independent informational resource. No spreadsheet, guide, or photo can guarantee the quality, authenticity, or transaction outcome of any purchase. Always verify information independently before buying.
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pillarArticles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="bg-background-100 rounded-lg p-4 border border-background-200 hover:border-primary-300 transition-colors"
                  >
                    <p className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors line-clamp-2">
                      {a.title}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{a.readTime}</p>
                  </Link>
                ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-primary-50 rounded-lg p-8 md:p-10 text-center mt-12">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              Keep Exploring
            </h3>
            <p className="text-sm text-foreground-600 mb-6 max-w-xl mx-auto">
              Return to the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">AllChinaBuy Spreadsheet</Link> homepage to browse categories, resources, and more guides.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('article.backToHome')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 text-sm font-semibold border-2 border-primary-500 text-primary-500 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                Visit Main Website
              </a>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-10 pt-6 border-t border-background-200 text-center">
            <p className="text-xs text-foreground-500">
              Last Updated: {article.updated} • This is an independent informational resource
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}